'use client';
import { productToCard } from '../../../product/index';
import ProductCardSkeleton from '../../server/product/card/ProductCardSkeleton';
import { toProductImagePreview } from '../../../product/productImagePreview';
import { Chunk, Option } from 'effect';
import { useRef } from 'react';

import ContentBlockProductsPagination from './ContentBlockProductsPagination';
import ProductCardRecommendationAddToCart from '../product/ProductCardRecommendationAddToCart';
import ProductCard from '../../server/product/card/ProductCard';
import ProductCardButtons from '../../server/product/card/ProductCardButtons';
import ProductCardHeader from '../../server/product/card/ProductCardHeader';
import ProductCardImage from '../../server/product/card/ProductCardImage';
import ProductCardMediaLink from '../../server/product/card/ProductCardMediaLink';
import ProductCardPriceStock from '../../server/product/card/ProductCardPriceStock';
import ProductCardStickers from '../../server/product/card/ProductCardStickers';
import ProductCardCoupon from '../../server/product/ProductCardCoupon';

import {
  changeContentBlockProductsPageAtomFnFamily,
  createContentBlockProductsPageKey,
  currentContentBlockProductsPageAtomFamily,
} from '@vs-shop/engine/atom/products/contentBlockProductsPagination';
import { fetchProductsByCodesPageAtom } from '../../../effect/atom/products/fetchProductsByCodesPage';
import { ProductCardVariant } from '@vs-shop/commerce-ui/productCard';
import {
  ProductsGrid,
  ProductsGridItem,
  ProductsGridVariant,
} from '@vs-shop/commerce-ui/productsGrid';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useAtomSet, useAtomValue } from '@effect/atom-react';

type Props = {
  blockIndex: number;
  codes: readonly string[];
  itemsPerPage: number;
  productCardVariant: ProductCardVariant;
  displayPagination: boolean;
};

export default function ContentBlockProductsGrid({
  blockIndex,
  codes,
  itemsPerPage,
  productCardVariant,
  displayPagination,
}: Props) {
  const pageKey = createContentBlockProductsPageKey(blockIndex);
  const currentPageAtom = currentContentBlockProductsPageAtomFamily(pageKey);
  const changePageAtomFn = changeContentBlockProductsPageAtomFnFamily(pageKey);

  const currentPage = useAtomValue(currentPageAtom);
  const changePage = useAtomSet(changePageAtomFn, {
    mode: 'promise',
  });

  const productsResponse = useAtomValue(
    fetchProductsByCodesPageAtom(codes, currentPage, itemsPerPage)
  );

  const ref = useRef<HTMLDivElement>(null);

  function scrollToProductsGrid(): void {
    if (!ref.current) return;

    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'center',
    });
  }

  return (
    <div ref={ref}>
      {/* A content block caps the listing field at four tracks — this block's own layout, not a third look. */}
      <ProductsGrid variant={ProductsGridVariant.LISTING} className="lap-lg:grid-cols-4">
        {AsyncResult.builder(productsResponse)
          .onWaiting(() =>
            Chunk.toReadonlyArray(Chunk.range(1, itemsPerPage)).map((i) => (
              <ProductsGridItem key={i}>
                <ProductCardSkeleton variant={productCardVariant} />
              </ProductsGridItem>
            ))
          )
          .onSuccess((response) => {
            const products = response['hydra:member'];
            const productCards = products.map((product) => productToCard(product));

            return productCards.map((productCard) => {
              return (
                <ProductsGridItem key={productCard.name}>
                  <ProductCard
                    variant={productCardVariant}
                    header={
                      <ProductCardHeader
                        productName={productCard.name}
                        routerUrl={productCard.routerUrl}
                        metaTitle={productCard.metaTitle}
                      />
                    }
                    media={
                      <ProductCardMediaLink
                        routerUrl={productCard.routerUrl}
                        metaTitle={productCard.metaTitle}
                        variant={productCardVariant}
                      >
                        <ProductCardImage
                          imagePath={productCard.imagePath}
                          alt={productCard.name}
                          variant={productCardVariant}
                        />
                        <ProductCardStickers
                          labels={productCard.labels}
                          ageRestriction={productCard.ageRestriction}
                          price={productCard.price}
                          originalPrice={productCard.originalPrice}
                        />
                      </ProductCardMediaLink>
                    }
                    promo={
                      <ProductCardCoupon
                        productCode={productCard.code}
                        productName={productCard.name}
                        discountBanner={productCard.discountBanner}
                        price={productCard.price}
                        priceWithoutTax={productCard.priceWithoutTax}
                        stock={productCard.stock}
                      />
                    }
                    description={productCard.subtitle ?? productCard.description}
                    footer={
                      <>
                        <ProductCardPriceStock
                          price={productCard.price}
                          originalPrice={productCard.originalPrice}
                          stock={productCard.stock}
                          labels={productCard.labels}
                          variant={productCardVariant}
                        />
                        {productCardVariant === ProductCardVariant.RECOMMENDATION ? (
                          <ProductCardRecommendationAddToCart
                            productCode={productCard.code}
                            productName={productCard.name}
                            imagePreview={toProductImagePreview(productCard.imagePath)}
                            ageRestriction={productCard.ageRestriction}
                            price={Option.getOrElse(productCard.price, () => 0)}
                            priceWithoutTax={Option.getOrElse(productCard.priceWithoutTax, () => 0)}
                            stock={productCard.stock}
                            className="w-full px-2 text-sm tracking-normal @[320px]:text-base"
                          />
                        ) : (
                          <ProductCardButtons
                            productCode={productCard.code}
                            productName={productCard.name}
                            isFavourite={productCard.isFavourite}
                            routerUrl={productCard.routerUrl}
                            metaTitle={productCard.metaTitle}
                            variant={productCardVariant}
                          />
                        )}
                      </>
                    }
                  />
                </ProductsGridItem>
              );
            });
          })
          .render()}
      </ProductsGrid>

      {displayPagination
        ? AsyncResult.builder(productsResponse)
            .onSuccess((response) =>
              Option.match(Option.fromNullishOr(response['hydra:view']), {
                onNone: () => null,
                onSome: (hydraViewExtended) => (
                  <ContentBlockProductsPagination
                    blockIndex={blockIndex}
                    hydraViewExtended={hydraViewExtended}
                    changePage={changePage}
                    scrollToProductsGrid={scrollToProductsGrid}
                  />
                ),
              })
            )
            .render()
        : null}
    </div>
  );
}
